/**
 * reportPerf — the web-vitals → perf-beacon bridge (ANL-PR3).
 *
 * `handleWebVital` is the callback handed to reportWebVitals(...) in index.js.
 * web-vitals reports each metric ONCE per page load with an object shaped
 * `{ name, value, ... }` (name is upper-case: 'LCP'|'CLS'|'FID'|'TTFB'|'FCP'|
 * 'INP'). We map the name to its PERF_METRICS member and POST a single-sample
 * perf batch; a web-vital with no place in PERF_METRICS (FCP) maps to null and is
 * SKIPPED. Each vital arrives separately, so each is its own tiny one-sample POST
 * (low frequency — once per load per metric — so batching buys nothing).
 *
 * Best-effort + silent: postPerf swallows errors and honors the disabled latch,
 * so a metric arriving after the flag-OFF 404 simply no-ops. We also guard the
 * token here so web-vitals collected before login (FCP/TTFB can fire early) are
 * dropped for guests rather than sent unauthenticated.
 *
 * `value` is rounded: ms vitals to the nearest integer ms (sub-ms precision is
 * noise the histogram buckets discard anyway); CLS, a small unitless ratio, is
 * rounded to 4 decimals so its fine buckets (0.01 / 0.05 / 0.1 …) stay meaningful.
 */

import { postPerf } from '../api/telemetry';
import { webVitalToMetric } from './perfMetrics';

function _hasToken() {
  try {
    return !!localStorage.getItem('token');
  } catch {
    return false;
  }
}

function _roundFor(metric, value) {
  if (!Number.isFinite(value)) return null;
  if (metric === 'cls') {
    // Unitless layout-shift ratio — keep 4 dp so the 0.01/0.05/0.1 buckets
    // aren't flattened by integer rounding.
    return Math.round(value * 10_000) / 10_000;
  }
  // Millisecond vitals — integer ms.
  return Math.round(value);
}

/**
 * Handle one web-vitals report. Maps it to a PERF_METRICS sample and beacons it.
 * Returns the metric name sent (for tests/debug) or null when skipped. NEVER
 * throws — a malformed report or a missing token is a quiet no-op.
 *
 * @param {{name?:string, value?:number}} report  a web-vitals metric object.
 */
export function handleWebVital(report) {
  if (!report || typeof report !== 'object') return null;
  const metric = webVitalToMetric(report.name);
  if (!metric) return null; // FCP or unrecognized — not in PERF_METRICS.
  if (!_hasToken()) return null; // guests aren't beaconed.

  const value = _roundFor(metric, Number(report.value));
  if (value === null || value < 0) return null;

  // One-sample batch. `context` (the route) is intentionally omitted — it's not
  // stored server-side, and a web-vital is a page-load quality signal, not a
  // per-route record. postPerf filters against PERF_METRICS again as a backstop.
  postPerf([{ metric, value }]);
  return metric;
}

export default handleWebVital;
